import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaSearch, 
  FaFilter, 
  FaDownload, 
  FaEye, 
  FaTrash, 
  FaEdit,
  FaCheck,
  FaTimes,
  FaClock,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaFileAlt,
  FaGraduationCap,
  FaUniversity,
  FaCalendarAlt,
  FaUserGraduate,
  FaUsers,
  FaUserTie,
  FaComments,
  FaStar
} from 'react-icons/fa';
import { ApplicantService } from '../../services/ApplicantService';
import { OpportunityService } from '../../services/OpportunityService';
import { getSaasPath } from '../../utils/environment';
import OpportunityGrid from '../OpportunityGrid/OpportunityGrid';
import { OpportunityDetailsModal, EditOpportunityModal } from '../SaasEditOpportunitySection/SaasEditOpportunitySection';
import { AuthContext } from '../../context/AuthContext';
import { canManageOpportunities } from '../../utils/permissions';

// Applicant Card Component
const ApplicantCard = ({ applicant, onStatusUpdate, onCreateComment, onDeleteComment, onDownloadResume, onDelete, onShowComments, onAddComment }) => {
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState(applicant.status);

  const handleStatusSubmit = () => {
    onStatusUpdate(applicant.id, newStatus);
    setShowStatusModal(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'reviewed': return 'bg-yellow-100 text-yellow-800';
      case 'shortlisted': return 'bg-purple-100 text-purple-800';
      case 'offered': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new': return <FaClock className="w-4 h-4" />;
      case 'reviewed': return <FaEye className="w-4 h-4" />;
      case 'shortlisted': return <FaStar className="w-4 h-4" />;
      case 'offered': return <FaCheck className="w-4 h-4" />;
      case 'rejected': return <FaTimes className="w-4 h-4" />;
      default: return <FaClock className="w-4 h-4" />;
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-3">
            <h4 className="text-lg font-semibold text-[#1B222A]">{applicant.name}</h4>
            <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor(applicant.status)}`}>
              {getStatusIcon(applicant.status)}
              <span className="capitalize">{applicant.status}</span>
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <FaEnvelope className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-[#1B222A]">{applicant.email}</span>
              </div>
              {applicant.contact_mobile && (
                <div className="flex items-center space-x-2">
                  <FaPhone className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-[#1B222A]">{applicant.contact_mobile}</span>
                </div>
              )}
              {applicant.country && (
                <div className="flex items-center space-x-2">
                  <FaMapMarkerAlt className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-[#1B222A]">{applicant.country}</span>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <FaUniversity className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-[#1B222A]">{applicant.university}</span>
              </div>
              <div className="flex items-center space-x-2">
                <FaGraduationCap className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-[#1B222A]">{applicant.department}</span>
              </div>
              <div className="flex items-center space-x-2">
                <FaCalendarAlt className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-[#1B222A]">
                  Applied: {new Date(applicant.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex space-x-2 ml-4">
          <button
            onClick={onShowComments}
            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
            title="View Comments"
          >
            <FaComments className="w-4 h-4" />
          </button>
          {applicant.resume_url && (
            <button
              onClick={() => onDownloadResume(applicant.id)}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Download Resume"
            >
              <FaDownload className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onAddComment}
            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
            title="Add Comment"
          >
            <FaEdit className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowStatusModal(true)}
            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            title="Update Status"
          >
            <FaCheck className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(applicant.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete Application"
          >
            <FaTrash className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-[#1B222A] mb-4">Update Application Status</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#1B222A] mb-2">Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent"
                >
                  <option value="new">New</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="shortlisted">Shortlisted</option>
                  <option value="offered">Offered</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowStatusModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-[#1B222A] hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusSubmit}
                className="flex-1 px-4 py-2 bg-[#DB4F40] text-white rounded-lg hover:bg-[#c13426] transition-colors"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SaasReviewOpportunitiesSection = () => {
  console.log('=== DEBUG: Component Re-render ===');
  console.log('Timestamp:', new Date().toISOString());
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    title: '',
    department: '',
    supervisor: ''
  });
  const [pendingFilters, setPendingFilters] = useState({
    title: '',
    department: '',
    supervisor: ''
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 30
  });
  const [pendingCounts, setPendingCounts] = useState({});
  const [totalApplicantCounts, setTotalApplicantCounts] = useState({});

  // Modal and selected opportunity state for view/edit
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, opportunity: null });

  // User context
  const { userDetails: currentUser, isUserDetailsLoading } = useContext(AuthContext);
  
  // Temporary fix: Use localStorage user if API user doesn't have proper permissions
  const localStorageUser = localStorage.getItem('user');
  const parsedLocalStorageUser = localStorageUser ? JSON.parse(localStorageUser) : null;
  
  // Use localStorage user if API user doesn't have role permissions
  const effectiveUser = (currentUser && currentUser.role?.permissions) ? currentUser : parsedLocalStorageUser;
  
  // Additional debug: Check if user is properly loaded
  console.log('=== ADDITIONAL DEBUG ===');
  console.log('currentUser exists:', !!currentUser);
  console.log('currentUser has role:', !!currentUser?.role);
  console.log('currentUser has permissions:', !!currentUser?.role?.permissions);
  console.log('parsedLocalStorageUser exists:', !!parsedLocalStorageUser);
  console.log('parsedLocalStorageUser has role:', !!parsedLocalStorageUser?.role);
  console.log('parsedLocalStorageUser has permissions:', !!parsedLocalStorageUser?.role?.permissions);
  console.log('effectiveUser will be:', effectiveUser === currentUser ? 'currentUser' : 'parsedLocalStorageUser');
  console.log('User ID from API:', currentUser?.id);
  console.log('User ID from localStorage:', parsedLocalStorageUser?.id);
  console.log('Role ID from API:', currentUser?.role?.id);
  console.log('Role ID from localStorage:', parsedLocalStorageUser?.role?.id);
  console.log('=== END ADDITIONAL DEBUG ===');
  
  // Debug: Log user details and permissions
  console.log('=== DEBUG: User Details ===');
  console.log('isUserDetailsLoading:', isUserDetailsLoading);
  console.log('currentUser:', currentUser);
  console.log('currentUser?.role:', currentUser?.role);
  console.log('currentUser?.role?.permissions:', currentUser?.role?.permissions);
  
  console.log('localStorage user:', parsedLocalStorageUser);
  console.log('localStorage user role permissions:', parsedLocalStorageUser?.role?.permissions);
  
  // Check if there's a mismatch
  if (currentUser && parsedLocalStorageUser) {
    console.log('=== COMPARISON ===');
    console.log('API user role permissions:', currentUser.role?.permissions);
    console.log('localStorage user role permissions:', parsedLocalStorageUser.role?.permissions);
    console.log('Permissions match:', JSON.stringify(currentUser.role?.permissions) === JSON.stringify(parsedLocalStorageUser.role?.permissions));
    console.log('=== END COMPARISON ===');
  }
  
  if (effectiveUser?.role?.permissions) {
    console.log('Permission details:');
    console.log('- view_opportunities:', effectiveUser.role.permissions.view_opportunities);
    console.log('- manage_opportunities:', effectiveUser.role.permissions.manage_opportunities);
    console.log('- manage_users:', effectiveUser.role.permissions.manage_users);
    console.log('- role_management:', effectiveUser.role.permissions.role_management);
  }
  console.log('canManageOpportunities result:', canManageOpportunities(effectiveUser));
  
  // Manual test with hardcoded user data
  const testUser = {
    role: {
      permissions: {
        manage_opportunities: true,
        manage_users: true,
        role_management: true
      }
    }
  };
  console.log('Manual test - canManageOpportunities with test user:', canManageOpportunities(testUser));
  console.log('=== END DEBUG ===');

  // Load opportunities on component mount and when filters change
  useEffect(() => {
    loadOpportunities();
  }, [filters, pagination.page]);

  useEffect(() => {
    // After opportunities are loaded, fetch pending review counts for each
    if (opportunities.length > 0) {
      fetchPendingCounts();
    }
  }, [opportunities]);

  const loadOpportunities = async () => {
    try {
      setLoading(true);
      const params = {
        title: filters.title,
        department: filters.department,
        supervisor: filters.supervisor,
        limit: pagination.limit,
        offset: (pagination.page - 1) * pagination.limit,
      };
      const response = await OpportunityService.getSaasOpportunities(params);
      if (response && response.data && Array.isArray(response.data)) {
        setOpportunities(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0
        }));
      } else {
        console.warn('No data in opportunities response:', response);
        setOpportunities([]);
        setPagination(prev => ({
          ...prev,
          total: 0
        }));
      }
    } catch (error) {
      console.error('Failed to load opportunities:', error);
      setOpportunities([]);
      setPagination(prev => ({
        ...prev,
        total: 0
      }));
    } finally {
      setLoading(false);
    }
  };

  // Fetch pending review counts and total applicants for each opportunity
  const fetchPendingCounts = async () => {
    const counts = {};
    const totalCounts = {};
    await Promise.all(
      opportunities.map(async (opp) => {
        try {
          const stats = await ApplicantService.getApplicantStats({ opportunity_id: opp.id });
          counts[opp.id] = stats?.status_counts?.new || 0;
          totalCounts[opp.id] = stats?.total_count || 0;
        } catch (e) {
          counts[opp.id] = 0;
          totalCounts[opp.id] = 0;
        }
      })
    );
    setPendingCounts(counts);
    setTotalApplicantCounts(totalCounts);
  };

  const handleReviewOpportunity = async (opportunity) => {
    // Navigate to the review applications page instead of opening modal
    navigate(getSaasPath(`/review/${opportunity.id}`));
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString();
  };

  const getFundingText = (fullyFunded) => {
    return fullyFunded ? 'Fully Funded' : 'Partial/Not Funded';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
      approved: { color: 'bg-green-100 text-green-800', text: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800', text: 'Rejected' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  // View handler: fetch from backend
  const handleViewOpportunity = async (opportunity) => {
    setDetailsLoading(true);
    setShowDetailsModal(true);
    try {
      const response = await OpportunityService.getSaasOpportunityById(opportunity.id);
      setSelectedOpportunity(response.data);
    } catch (error) {
      setShowDetailsModal(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Delete handler: call backend (validation handled by backend)
  const handleDeleteOpportunity = async (opportunity) => {
    if (!window.confirm('Are you sure you want to delete this opportunity?')) return;
    try {
      await OpportunityService.deleteSaasOpportunityById(opportunity.id);
      // Refresh list
      setOpportunities((prev) => prev.filter((o) => o.id !== opportunity.id));
    } catch (error) {
      // Optionally show error
    }
  };

  // Edit handler: open modal
  const handleEditOpportunity = async (opportunity) => {
    try {
      const response = await OpportunityService.getSaasOpportunityById(opportunity.id);
      setEditModal({ open: true, opportunity: response.data });
    } catch (error) {
      // Optionally show error
    }
  };

  // Edit submit handler
  const handleEditSubmit = async (updatedData) => {
    try {
      await OpportunityService.updateSaasOpportunityById(editModal.opportunity.id, updatedData);
      setEditModal({ open: false, opportunity: null });
      // Refresh the opportunities list
      const params = {
        title: filters.title,
        department: filters.department,
        supervisor: filters.supervisor,
        limit: pagination.limit,
        offset: (pagination.page - 1) * pagination.limit,
      };
      const response = await OpportunityService.getSaasOpportunities(params);
      setOpportunities(response.data || []);
      setPagination((prev) => ({ ...prev, total: response.pagination?.total || 0 }));
    } catch (error) {
      throw error;
    }
  };

  // Show loading if user details are still loading
  if (isUserDetailsLoading) {
    console.log('=== DEBUG: User details still loading ===');
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFEEED] py-8 px-4 sm:px-6 lg:px-8">
      <div>
        <div className="max-w-6xl mx-auto mb-8">
          <h1 className="text-3xl font-extrabold text-[#DB4F40] mb-6">Review Opportunities</h1>
        </div>
        <div className="relative max-w-6xl mx-auto">
          <div className="p-8 sm:p-12">
            {/* Filter Section */}
            <div className="max-w-6xl mx-auto">
              <div className="border border-[#FFD6D4] overflow-hidden mb-8 rounded-3xl bg-white">
                <div className="p-8 sm:p-12">
                  <h2 className="text-2xl font-bold text-[#DB4F40] mb-6">Filter Opportunities</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#1B222A] mb-2">Title</label>
                      <div className="relative">
                        <FaUserGraduate className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#DB4F40] w-4 h-4" />
                        <input 
                          type="text" 
                          name="title" 
                          value={pendingFilters.title} 
                          onChange={e => setPendingFilters(f => ({ ...f, title: e.target.value }))} 
                          placeholder="Filter by Title" 
                          className="w-full pl-10 pr-4 py-2 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md" 
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#1B222A] mb-2">Department</label>
                      <div className="relative">
                        <FaUsers className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#DB4F40] w-4 h-4" />
                        <input 
                          type="text" 
                          name="department" 
                          value={pendingFilters.department} 
                          onChange={e => setPendingFilters(f => ({ ...f, department: e.target.value }))} 
                          placeholder="Filter by Department" 
                          className="w-full pl-10 pr-4 py-2 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md" 
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#1B222A] mb-2">Supervisor</label>
                      <div className="relative">
                        <FaUserTie className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#DB4F40] w-4 h-4" />
                        <input 
                          type="text" 
                          name="supervisor" 
                          value={pendingFilters.supervisor} 
                          onChange={e => setPendingFilters(f => ({ ...f, supervisor: e.target.value }))} 
                          placeholder="Filter by Supervisor" 
                          className="w-full pl-10 pr-4 py-2 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md" 
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={() => {
                        setFilters({ ...pendingFilters });
                        setPagination(p => ({ ...p, page: 1 }));
                      }}
                      className="px-6 py-2 rounded-lg bg-[#DB4F40] text-white font-semibold hover:bg-[#C64537] transition-all shadow-sm mr-2"
                      type="button"
                    >
                      Search
                    </button>
                    <button
                      onClick={() => {
                        setPendingFilters({ title: '', department: '', supervisor: '' });
                        setFilters({ title: '', department: '', supervisor: '' });
                        setPagination(p => ({ ...p, page: 1 }));
                      }}
                      className="px-6 py-2 rounded-lg bg-[#DB4F40] text-white font-semibold hover:bg-[#C64537] transition-all shadow-sm"
                      type="button"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Opportunities List Section - Card/Grid Layout */}
              <div className="overflow-hidden rounded-3xl border border-0 md:border md:border-[#FFD6D4] bg-white mt-2 mb-2 sm:mt-4 sm:mb-4">
                <div className="p-2 sm:p-8 lg:p-12">
                  <h2 className="text-2xl font-bold text-[#DB4F40] mb-6">Opportunities with Applications</h2>
                  {opportunities.length === 0 ? (
                    <div className="py-12 text-center text-gray-500">
                      No opportunities found. Try adjusting your filters.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-8">
                      {opportunities.map((opportunity) => (
                        <div key={opportunity.id} className="relative">
                          <div
                            className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 group mx-0 md:mx-[5%] relative border-0 md:border-4 md:hover:border-[#DB4F40] md:border-[#FFD6D4] mt-4 mb-4 sm:mt-6 sm:mb-6"
                          >
                            <div className="p-4 sm:p-6 flex flex-col md:flex-row gap-6">
                              {/* University Logo */}
                              <div className="flex-shrink-0 flex flex-col justify-center items-center h-full md:h-auto md:justify-center bg-gradient-to-br from-[#FFBEB9] to-[#FFD1CE] rounded-xl" style={{ flex: '0 0 15%' }}>
                                <div className="w-24 h-24 flex-shrink-0 flex items-center justify-center">
                                  <img
                                    src={`${process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : ''}/university/${opportunity.university.replace(/ /g, '%20')}.png`}
                                    alt={opportunity.university}
                                    className="w-full h-full object-contain transition-opacity duration-300 hover:opacity-80"
                                    onError={e => { e.target.style.display = 'none'; }}
                                  />
                                </div>
                              </div>
                              {/* Main Content */}
                              <div className="flex-1 min-w-0 flex flex-col gap-2">
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                                  <h3 className="text-lg font-bold text-[#1B222A] hover:text-[#DB4F40] transition-colors cursor-pointer group-hover:text-[#DB4F40]">
                                    {opportunity.title}
                                  </h3>
                                  {opportunity.fully_funded && (
                                    <div className="flex flex-wrap gap-2">
                                      <span className="px-3 py-1 bg-[#5AC1E4]/20 text-[#1B222A] rounded-full text-xs font-medium">
                                        Fully Funded
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-base">
                                  <div className="flex items-center">
                                    <FaGraduationCap className="text-[#DB4F40] mr-2" />
                                    <span className="font-semibold text-[#6C757D]">{opportunity.department}</span>
                                  </div>
                                  <div className="flex items-center">
                                    <FaUserTie className="text-[#DB4F40] mr-2" />
                                    <span className="font-semibold text-[#6C757D]">{opportunity.supervisor || 'N/A'}</span>
                                  </div>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-base">
                                  <div className="flex items-center">
                                    <FaCalendarAlt className="text-[#DB4F40] mr-2" />
                                    <span className="font-semibold text-[#6C757D]">{formatDate(opportunity.closing_date)}</span>
                                  </div>
                                  <div className="flex items-center">
                                    <FaUser className="text-green-600 mr-2" />
                                    <span className="font-semibold text-green-800">Total Applicants: {totalApplicantCounts[opportunity.id] ?? 0}</span>
                                  </div>
                                  <div className="flex items-center">
                                    <FaUser className="text-blue-600 mr-2" />
                                    <span className="font-semibold text-blue-800">Pending Reviews: {pendingCounts[opportunity.id] ?? 0}</span>
                                  </div>
                                </div>
                                {opportunity.ai_summary && (
                                  <div className="mt-2 text-[#1B222A]/80 text-base leading-relaxed">
                                    {opportunity.ai_summary}
                                  </div>
                                )}
                                <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-4">
                                  <button
                                    className="flex items-center px-4 py-2 bg-[#FFD6D4] text-[#DB4F40] rounded-lg hover:bg-[#FFEEED] transition-colors text-sm font-medium"
                                    onClick={() => handleReviewOpportunity(opportunity)}
                                    title="Review Applications"
                                  >
                                    <FaEye className="w-4 h-4 mr-2" />
                                    Review Applications
                                  </button>
                                  <button
                                    className="flex items-center px-4 py-2 bg-[#FFD6D4] text-[#DB4F40] rounded-lg hover:bg-[#FFEEED] transition-colors text-sm font-medium"
                                    onClick={() => handleViewOpportunity(opportunity)}
                                    title="View Opportunity"
                                  >
                                    <FaEye className="w-4 h-4 mr-2" />
                                    View
                                  </button>
                                  {/* Debug: Log permission check for this specific opportunity */}
                                  {(() => {
                                    console.log(`=== DEBUG: Before permission check for opportunity ${opportunity.id} ===`);
                                    console.log('effectiveUser passed to canManageOpportunities:', effectiveUser);
                                    console.log('effectiveUser.role:', effectiveUser?.role);
                                    console.log('effectiveUser.role.permissions:', effectiveUser?.role?.permissions);
                                    const hasPermission = canManageOpportunities(effectiveUser);
                                    console.log(`=== DEBUG: Opportunity ${opportunity.id} ===`);
                                    console.log('effectiveUser type:', typeof effectiveUser);
                                    console.log('effectiveUser structure:', JSON.stringify(effectiveUser, null, 2));
                                    console.log('effectiveUser.role.permissions.manage_opportunities:', effectiveUser?.role?.permissions?.manage_opportunities);
                                    console.log('effectiveUser.role.permissions.manage_opportunities === true:', effectiveUser?.role?.permissions?.manage_opportunities === true);
                                    console.log('canManageOpportunities result:', hasPermission);
                                    console.log('Will show edit/delete buttons:', hasPermission);
                                    console.log('=== END DEBUG ===');
                                    return hasPermission;
                                  })() && (
                                    <>
                                      <button
                                        className="flex items-center px-4 py-2 bg-[#FFD6D4] text-[#DB4F40] rounded-lg hover:bg-[#FFEEED] transition-colors text-sm font-medium"
                                        onClick={() => handleEditOpportunity(opportunity)}
                                        title="Edit Opportunity"
                                      >
                                        <FaEdit className="w-4 h-4 mr-2" />
                                        Edit
                                      </button>
                                      <button
                                        className="flex items-center px-4 py-2 bg-[#FFD6D4] text-[#DB4F40] rounded-lg hover:bg-[#FFEEED] transition-colors text-sm font-medium"
                                        onClick={() => handleDeleteOpportunity(opportunity)}
                                        title="Delete Opportunity"
                                      >
                                        <FaTrash className="w-4 h-4 mr-2" />
                                        Delete
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Pagination Controls */}
                  {pagination.total > pagination.limit && (
                    <div className="flex flex-col sm:flex-row justify-center items-center gap-4 py-8">
                      <button
                        onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
                        disabled={pagination.page === 1}
                        className="px-6 py-2 rounded-lg bg-white border border-[#FFD6D4] hover:border-[#DB4F40] text-[#DB4F40] hover:bg-[#FFEEED] disabled:opacity-50 transition-all"
                      >
                        Previous
                      </button>
                      <span className="px-6 py-2 bg-[#DB4F40] text-white rounded-lg shadow-sm">
                        Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
                      </span>
                      <button
                        onClick={() => setPagination(p => ({ ...p, page: Math.min(Math.ceil(pagination.total / pagination.limit), p.page + 1) }))}
                        disabled={pagination.page === Math.ceil(pagination.total / pagination.limit)}
                        className="px-6 py-2 rounded-lg bg-white border border-[#FFD6D4] hover:border-[#DB4F40] text-[#DB4F40] hover:bg-[#FFEEED] disabled:opacity-50 transition-all"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* View Opportunity Modal */}
      {showDetailsModal && (
        <OpportunityDetailsModal
          opportunity={selectedOpportunity}
          loading={detailsLoading}
          onClose={() => setShowDetailsModal(false)}
        />
      )}
      {/* Edit Opportunity Modal */}
      {editModal.open && (
        <EditOpportunityModal
          opportunity={editModal.opportunity}
          onClose={() => setEditModal({ open: false, opportunity: null })}
          onSubmit={handleEditSubmit}
        />
      )}
    </div>
  );
};

export default SaasReviewOpportunitiesSection; 